import { FormEvent, useEffect, useMemo, useState } from "react";
import axios from "../utils/axios";
import {
  formatChannelOptionLabel,
  sortChannelsByCategoryAndName,
} from "../utils/channelOptions";
import CollapsibleSection from "./CollapsibleSection";
import Spinner from "./Spinner";
import "./LiveNotifications.css";
import "./DropNotifications.css";

interface Channel {
  id: string;
  name: string;
  type?: number;
  categoryName?: string | null;
}

interface DropNotification {
  id: number;
  guild_id: string;
  channel_id: string;
  game_id: string;
  game_name: string;
  message: string;
  show_game_embed: number;
  last_drop_start_at?: string | null;
  last_drop_end_at?: string | null;
}

interface DropNotificationsProps {
  guildId: string;
  channels: Channel[];
}

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const pad = (num: number) => `${num}`.padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

function DropNotifications({ guildId, channels }: DropNotificationsProps) {
  const [notifications, setNotifications] = useState<DropNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formGameName, setFormGameName] = useState("");
  const [formChannelId, setFormChannelId] = useState("");
  const [formMessage, setFormMessage] = useState(
    "New Twitch Drops are now available!",
  );
  const [formShowEmbed, setFormShowEmbed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const textChannels = useMemo(
    () =>
      sortChannelsByCategoryAndName(
        (channels || []).filter(
          (channel) => channel.type === 0 || channel.type === 5,
        ),
      ),
    [channels],
  );

  useEffect(() => {
    fetchNotifications();
  }, [guildId]);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `/api/server/${guildId}/drop-notifications`,
      );
      setNotifications(response.data.notifications || []);
    } catch (err) {
      console.error("Failed to load drop notifications", err);
      setError("Failed to load drop notifications. Please try again later.");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormGameName("");
    setFormChannelId("");
    setFormMessage("New Twitch Drops are now available!");
    setFormShowEmbed(true);
    setEditingId(null);
    setShowForm(false);
  };

  const startCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (notification: DropNotification) => {
    setFormGameName(notification.game_name);
    setFormChannelId(notification.channel_id);
    setFormMessage(notification.message);
    setFormShowEmbed(notification.show_game_embed === 1);
    setEditingId(notification.id);
    setShowForm(true);
  };

  const getChannelName = (id: string): string => {
    const channel = textChannels.find((chan) => chan.id === id);
    return channel ? formatChannelOptionLabel(channel) : `#${id}`;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!formChannelId || !formGameName.trim() || !formMessage.trim()) {
      alert("Please complete every field.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (editingId) {
        await axios.put(
          `/api/server/${guildId}/drop-notifications/${editingId}`,
          {
            channelId: formChannelId,
            message: formMessage.trim(),
            showGameEmbed: formShowEmbed,
          },
        );
      } else {
        await axios.post(`/api/server/${guildId}/drop-notifications`, {
          channelId: formChannelId,
          gameName: formGameName.trim(),
          message: formMessage.trim(),
          showGameEmbed: formShowEmbed,
        });
      }

      await fetchNotifications();
      resetForm();
    } catch (err: any) {
      console.error("Failed to save drop notification", err);
      setError(
        err?.response?.data?.error ||
          "Failed to save drop notification. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Remove this Twitch Drop notification?")) {
      return;
    }

    setDeletingId(id);
    setError(null);
    try {
      await axios.delete(`/api/server/${guildId}/drop-notifications/${id}`);
      await fetchNotifications();
    } catch (err: any) {
      console.error("Failed to delete drop notification", err);
      setError(
        err?.response?.data?.error ||
          "Failed to delete drop notification. Please try again.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <CollapsibleSection title="🎁 Twitch Drops" count={notifications.length}>
      <div className="drop-notifications">
        <div className="drop-header">
          <div>
            <h4>Automatic drop alerts</h4>
            <p>
              Watch specific games and announce new Twitch Drop campaigns as soon
              as they go live.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={startCreate}>
            {editingId ? "Edit drop" : "Add drop"}
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-container">
            <Spinner />
            <p>Loading drop notifications…</p>
          </div>
        ) : notifications.length === 0 ? (
          <p className="empty-state">
            No Twitch Drop notifications configured yet. Add one to get started.
          </p>
        ) : (
          <div className="live-table-wrapper">
            <table className="live-table drop-table">
              <thead>
                <tr>
                  <th>Game name</th>
                  <th>Channel</th>
                  <th>Date start</th>
                  <th>Date end</th>
                  <th>Edit</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.id}>
                    <td>{notification.game_name}</td>
                    <td>{getChannelName(notification.channel_id)}</td>
                    <td>{formatDate(notification.last_drop_start_at)}</td>
                    <td>{formatDate(notification.last_drop_end_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-secondary btn-compact"
                        onClick={() => startEdit(notification)}
                      >
                        Edit
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger btn-compact"
                        onClick={() => handleDelete(notification.id)}
                        disabled={deletingId === notification.id}
                      >
                        {deletingId === notification.id ? "Removing…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showForm && (
          <form className="drop-form" onSubmit={handleSubmit}>
            <div className="drop-form__header">
              <h4>{editingId ? "Edit drop notification" : "Add drop notification"}</h4>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
                disabled={submitting}
              >
                Close
              </button>
            </div>
            <div className="drop-form__grid">
              <label>
                Game name
                <input
                  type="text"
                  value={formGameName}
                  onChange={(event) => setFormGameName(event.target.value)}
                  placeholder="PUBG: BATTLEGROUNDS"
                  disabled={Boolean(editingId)}
                />
              </label>
              <label>
                Discord channel
                <select
                  value={formChannelId}
                  onChange={(event) => setFormChannelId(event.target.value)}
                >
                  <option value="">Select a channel…</option>
                  {textChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {formatChannelOptionLabel(channel)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Message
              <textarea
                maxLength={200}
                rows={3}
                value={formMessage}
                onChange={(event) => setFormMessage(event.target.value)}
              />
            </label>
            <label className="drop-toggle">
              <input
                type="checkbox"
                checked={formShowEmbed}
                onChange={(event) => setFormShowEmbed(event.target.checked)}
              />
              <span>
                Show campaign overview embed before the reward breakdown
                (recommended)
              </span>
            </label>
            <div className="drop-form__actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? "Saving…" : editingId ? "Save changes" : "Add drop"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </CollapsibleSection>
  );
}

export default DropNotifications;
